import type { StaticImageData } from 'next/image';
import supplierHubPng from '@Assets/navigation/supplier-hub.png';
import supplierHub from '@Assets/navigation/supplier-hub.svg';
import products from '@Assets/navigation/products.svg';
import dashboard from '@Assets/navigation/dashboard.svg';
import suppliers from '@Assets/navigation/suppliers.svg';
// navigation.constants
import productScout from '@Assets/navigation/product-scout.png';
import nicheIdeas from '@Assets/navigation/niche-ideas.svg';
import productIdeas from '@Assets/navigation/product-ideas.svg';
import productSearch from '@Assets/navigation/search.svg';
import calculator from '@Assets/navigation/calculator.svg';
import boxSearch from '@Assets/icons/box-search.svg';
import boxesSearch from '@Assets/icons/boxes.svg';
import box from '@Assets/icons/box.svg';

export interface NavItem {
    href: string;
    icon: StaticImageData;
    labelKey: string;
    descriptionKey?: string;
    isAction?: boolean;
}

export interface ServiceItem extends Omit<NavItem, 'icon'> {
    readonly icon: StaticImageData;
    readonly isEnabled: boolean;
    readonly isBeta?: boolean;
    readonly defaultHref: string;
    readonly navigationItems: readonly NavItem[];
}

export type RouteKey = keyof typeof ROUTES;
export type RouteValue = typeof ROUTES[RouteKey];
export type PageAnchor = typeof PageAnchors[keyof typeof PageAnchors];


export const ROUTES = {
    // Protected
    ONBOARDING: '/onboarding',
    SETTINGS: '/settings',
    SETTINGS_ACCOUNT: '/settings/account',
    SETTINGS_SUBSCRIPTION: '/settings/subscription',
    SETTINGS_PERSONALIZATION: '/settings/personalization',
        // Supplier Hub
    HUB: '/hub',
    PRODUCTS: '/hub/products',
    SUPPLIERS: '/hub/suppliers',
    DASHBOARD: '/hub/dashboard',

        // ProductScout
    SCOUT: '/scout',
    IDEAS: '/scout/ideas',
    SEARCH: '/scout/research',
    NICHE_ANALYSIS_HISTORY: '/scout/niche-history',

        // Profit Calculator
    CALCULATOR: '/calculator',

    // Public
    LOGIN: '/login',
    MAGIC_LINK_VERIFY: '/auth/verify',
    RESET_PASSWORD: '/auth/reset-password',
    PRIVACY_POLICY: '/legal-information/privacy-policy',
    TERMS_AND_CONDITIONS: '/legal-information/tos',
} as const;

export const Services: readonly ServiceItem[] = [
        {
        href: ROUTES.SCOUT,
        icon: productScout,
        labelKey: 'services.productScout.title',
        descriptionKey: 'services.productScout.description',
        isEnabled: true,
        defaultHref: ROUTES.SEARCH,
        navigationItems: [
            { href: ROUTES.SEARCH, icon: boxSearch, labelKey: 'newResearch', isAction: true },
            { href: ROUTES.SEARCH, icon: box, labelKey: 'search' },
            { href: ROUTES.IDEAS, icon: productIdeas, labelKey: 'ideas' },
            { href: ROUTES.NICHE_ANALYSIS_HISTORY, icon: nicheIdeas, labelKey: 'nicheAnalysisHistory' },
        ] as const
    },
    {
        href: ROUTES.HUB,
        icon: supplierHubPng,
        labelKey: 'services.supplierHub.title',
        descriptionKey: 'services.supplierHub.description',
        isEnabled: true,
        isBeta: true,
        defaultHref: ROUTES.PRODUCTS,
        navigationItems: [
            // { href: ROUTES.DASHBOARD, icon: dashboard, labelKey: 'dashboard' },
            { href: ROUTES.PRODUCTS, icon: boxesSearch, labelKey: 'products' },
            // { href: ROUTES.SUPPLIERS, icon: suppliers, labelKey: 'suppliers' },
        ] as const
    },
    // {
    //     href: ROUTES.CALCULATOR,
    //     icon: calculator,
    //     labelKey: 'services.calculator.title',
    //     descriptionKey: 'services.calculator.description',
    //     isEnabled: true,
    //     defaultHref: ROUTES.CALCULATOR,    
    //     navigationItems: [{ href: ROUTES.CALCULATOR, icon: boxesSearch, labelKey: 'CALCULATOR' },] as const,
    // }
] as const;

export const PageAnchors = {
    RequirementsActionsSection: 'requirements_actions_section',
    OutreachEmailActionsSection: 'outreach_email_actions_section',
    SubscriptionPlans: 'subscription_plans',
} as const;

export const ACTIVE_SERVICE_COOKIE = 'active_service';

export const SETTINGS_ROUTES = [ROUTES.SETTINGS, ROUTES.SETTINGS_ACCOUNT] as const;
export const isSettingsRoute = (pathname: string) =>
    SETTINGS_ROUTES.some(r => pathname.startsWith(r));


export const getFirstEnabledService = () => {
    const s = Services.find(s => s.isEnabled);
    if (!s) throw new Error('No enabled services found. At least one service must be enabled.');
    return s;
};

export const findEnabledServiceByHref = (href: string) =>
    Services.find(s => s.href === href && s.isEnabled);

export const findEnabledServiceByPathname = (pathname: string) => {
    const seg = `/${pathname.split('/')[1] || ''}`;
    return findEnabledServiceByHref(seg);
};